import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBookCall } from "./BookCallDialog";
import { LangSwitcher } from "./LangSwitcher";
import { Logo } from "./Logo";
import { Button } from "./ui/button";
import { LogOut, User as UserIcon, Menu, X } from "lucide-react";

const NAV_LABELS = {
  en: {
    services: "Services",
    how: "How it works",
    faq: "FAQ",
    login: "Sign in",
    book: "Book consultation",
    dashboard: "My bookings",
    admin: "Admin",
    logout: "Sign out",
    language: "Language",
    explore: "Explore pages",
    drawerServices: "Services & packages",
    drawerIkea: "IKEA Assembly",
    drawerFaq: "FAQ page",
    drawerMore: "Blog & insights",
  },
  de: {
    services: "Leistungen",
    how: "Ablauf",
    faq: "FAQ",
    login: "Anmelden",
    book: "Beratung buchen",
    dashboard: "Meine Anfragen",
    admin: "Admin",
    logout: "Abmelden",
    language: "Sprache",
    explore: "Weitere Seiten",
    drawerServices: "Leistungen & Pakete",
    drawerIkea: "IKEA Möbelmontage",
    drawerFaq: "FAQ-Seite",
    drawerMore: "Blog & Insights",
  },
};

const PAGE_PATHS = {
  en: { services: "/services", ikea: "/ikea-assembly-munich", faq: "/faq", more: "/more" },
  de: { services: "/de/leistungen", ikea: "/ikea-moebelmontage-muenchen", faq: "/de/faq", more: "/de/mehr" },
};

export const SiteHeader = () => {
  const { user, logout } = useAuth();
  const { openDialog } = useBookCall();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerRef = useRef(null);

  const isDe =
    location.pathname === "/de" ||
    location.pathname.startsWith("/de/") ||
    location.pathname === "/ikea-moebelmontage-muenchen";
  const lang = isDe ? "de" : "en";
  const labels = NAV_LABELS[lang];
  const pages = PAGE_PATHS[lang];
  const homePath = isDe ? "/de" : "/";

  const goToSection = (id) => (e) => {
    e.preventDefault();
    setDrawerOpen(false);
    if (location.pathname === homePath) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState({}, "", `${homePath}#${id}`);
    } else {
      navigate(`${homePath}#${id}`);
    }
  };

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Close drawer on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (
        drawerRef.current &&
        !drawerRef.current.contains(e.target) &&
        !e.target.closest('[data-testid="menu-toggle"]')
      ) {
        setDrawerOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  return (
    <>
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FAF9F6]/85 border-b border-[#E5E2DC]" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-10 py-4 flex items-center justify-between gap-3">
          <Logo to={homePath} />

          {/* Inline anchor nav — visible from md+ */}
          <nav className="hidden md:flex items-center gap-5 lg:gap-7 text-sm whitespace-nowrap" data-testid="primary-nav">
            <a href={`${homePath}#services`} onClick={goToSection("services")} className="text-[#5C635A] hover:text-[#1C3326] transition-colors cursor-pointer" data-testid="nav-services">{labels.services}</a>
            <a href={`${homePath}#how-it-works`} onClick={goToSection("how-it-works")} className="text-[#5C635A] hover:text-[#1C3326] transition-colors cursor-pointer" data-testid="nav-how">{labels.how}</a>
            <a href={`${homePath}#faq`} onClick={goToSection("faq")} className="text-[#5C635A] hover:text-[#1C3326] transition-colors cursor-pointer" data-testid="nav-faq">{labels.faq}</a>
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden md:block">
              <LangSwitcher />
            </div>
            {user ? (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate(user.role === "admin" ? "/admin" : "/dashboard")}
                  className="hidden md:inline-flex rounded-sm text-[#1C3326] hover:bg-[#F5F3EF]"
                  data-testid="header-dashboard-btn"
                >
                  <UserIcon className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  {user.role === "admin" ? labels.admin : labels.dashboard}
                </Button>
                <Button
                  variant="ghost"
                  onClick={async () => { await logout(); navigate(homePath); }}
                  className="hidden md:inline-flex rounded-sm text-[#5C635A] hover:bg-[#F5F3EF]"
                  data-testid="header-logout-btn"
                >
                  <LogOut className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/login")}
                  className="hidden md:inline-flex rounded-sm text-[#1C3326] hover:bg-[#F5F3EF]"
                  data-testid="header-login-btn"
                >
                  {labels.login}
                </Button>
                <Button
                  onClick={() => openDialog({ source: `header-${lang}` })}
                  className="hidden md:inline-flex rounded-sm bg-[#1C3326] hover:bg-[#2A4A38] text-[#FAF9F6]"
                  data-testid="header-book-btn"
                >
                  {labels.book}
                </Button>
              </>
            )}

            {/* Hamburger — visible at all widths */}
            <button
              onClick={() => setDrawerOpen((v) => !v)}
              className="p-2 text-[#1C3326] hover:bg-[#F5F3EF] transition-colors border border-[#E5E2DC] hover:border-[#1C3326]"
              data-testid="menu-toggle"
              aria-label="Toggle full menu"
              aria-expanded={drawerOpen}
            >
              {drawerOpen ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Menu className="w-5 h-5" strokeWidth={1.5} />}
            </button>
          </div>
        </div>
      </header>

      {/* Drawer overlay */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex" data-testid="menu-drawer-overlay">
          <div className="flex-1 bg-[#1A201C]/40 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
          <aside
            ref={drawerRef}
            className="w-full sm:w-[420px] h-full bg-[#FAF9F6] shadow-2xl flex flex-col overflow-y-auto"
            data-testid="menu-drawer"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E2DC]">
              <span className="font-serif text-lg text-[#1A201C]">MUConcierge</span>
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-2 text-[#1C3326] hover:bg-[#F5F3EF]"
                aria-label="Close menu"
                data-testid="drawer-close"
              >
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>

            <nav className="px-6 py-6 flex flex-col">
              {/* Primary anchor links (mirrored from inline nav for small phones) */}
              <p className="tag-line text-[#C26D5C] mb-3 md:hidden">{lang === "de" ? "Navigation" : "Navigation"}</p>
              <a href={`${homePath}#services`} onClick={goToSection("services")} className="md:hidden py-3 border-b border-[#E5E2DC] text-[#1A201C]" data-testid="drawer-nav-services">{labels.services}</a>
              <a href={`${homePath}#how-it-works`} onClick={goToSection("how-it-works")} className="md:hidden py-3 border-b border-[#E5E2DC] text-[#1A201C]" data-testid="drawer-nav-how">{labels.how}</a>
              <a href={`${homePath}#faq`} onClick={goToSection("faq")} className="md:hidden py-3 border-b border-[#E5E2DC] text-[#1A201C]" data-testid="drawer-nav-faq">{labels.faq}</a>

              {/* Dedicated pages */}
              <p className="tag-line text-[#C26D5C] mt-2 md:mt-0 mb-3">{labels.explore}</p>
              <Link to={pages.services} className="py-3 border-b border-[#E5E2DC] text-[#1A201C] hover:text-[#1C3326]" data-testid="drawer-page-services">{labels.drawerServices}</Link>
              <Link to={pages.ikea} className="py-3 border-b border-[#E5E2DC] text-[#1A201C] hover:text-[#1C3326]" data-testid="drawer-page-ikea">{labels.drawerIkea}</Link>
              <Link to={pages.faq} className="py-3 border-b border-[#E5E2DC] text-[#1A201C] hover:text-[#1C3326]" data-testid="drawer-page-faq">{labels.drawerFaq}</Link>
              <Link to={pages.more} className="py-3 border-b border-[#E5E2DC] text-[#1A201C] hover:text-[#1C3326]" data-testid="drawer-page-more">{labels.drawerMore}</Link>

              {/* Utility on phones (md hides the inline ones) */}
              <div className="py-3 border-b border-[#E5E2DC] md:hidden flex items-center justify-between">
                <span className="text-xs tag-line text-[#5C635A]">{labels.language}</span>
                <LangSwitcher />
              </div>
              {user ? (
                <>
                  <button
                    onClick={() => navigate(user.role === "admin" ? "/admin" : "/dashboard")}
                    className="md:hidden py-3 border-b border-[#E5E2DC] text-[#1A201C] text-left"
                    data-testid="drawer-dashboard"
                  >
                    {user.role === "admin" ? labels.admin : labels.dashboard}
                  </button>
                  <button
                    onClick={async () => { await logout(); navigate(homePath); }}
                    className="md:hidden py-3 border-b border-[#E5E2DC] text-[#1A201C] text-left"
                    data-testid="drawer-logout"
                  >
                    {labels.logout}
                  </button>
                </>
              ) : (
                <Link to="/login" className="md:hidden py-3 border-b border-[#E5E2DC] text-[#1A201C]" data-testid="drawer-login">{labels.login}</Link>
              )}

              <Button
                onClick={() => { openDialog({ source: `drawer-${lang}` }); setDrawerOpen(false); }}
                className="mt-6 rounded-sm bg-[#1C3326] hover:bg-[#2A4A38] text-[#FAF9F6] w-full py-6"
                data-testid="drawer-book"
              >
                {labels.book}
              </Button>
            </nav>
          </aside>
        </div>
      )}
    </>
  );
};
